import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { ThreadSummary } from '@mailpilot/shared';
import { api, type ResOf } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';

export type ApiView = 'inbox' | 'flagged' | 'drafts' | 'sent' | 'needs_reply' | 'folder';
export type ThreadFilter = 'unread' | 'attachments' | 'mine' | 'unassigned' | 'followups';
export interface ThreadListParams {
  view: ApiView;
  mailboxId?: string;
  folderId?: string;
  q?: string;
  filter?: ThreadFilter;
  category?: string;
}
type ListRes = ResOf<'GET /api/threads'>;
export type ThreadDetail = ResOf<'GET /api/threads/:id'>;

const PAGE = '40';

export function useThreadList(p: ThreadListParams) {
  const params = { view: p.view, mailboxId: p.mailboxId, folderId: p.folderId, q: p.q || undefined, filter: p.filter, category: p.category };
  return useInfiniteQuery({
    queryKey: qk.threads(params),
    queryFn: ({ pageParam, signal }) => api('GET /api/threads', { query: { ...params, cursor: pageParam ?? undefined, limit: PAGE }, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: p.view !== 'folder' || !!p.folderId,
  });
}

export function useThread(id: string | undefined) {
  return useQuery({
    queryKey: qk.thread(id ?? 'none'),
    queryFn: ({ signal }) => api('GET /api/threads/:id', { params: { id: id! }, signal }),
    enabled: !!id,
  });
}

/** Apply a patch to a thread summary everywhere it is cached (lists + detail). */
function patchThreadCaches(qc: ReturnType<typeof useQueryClient>, id: string, patch: Partial<ThreadSummary>) {
  qc.setQueriesData<InfiniteData<ListRes>>({ queryKey: qk.threadsRoot }, (prev) =>
    prev ? { ...prev, pages: prev.pages.map((pg) => ({ ...pg, threads: pg.threads.map((t) => (t.id === id ? { ...t, ...patch } : t)) })) } : prev,
  );
  qc.setQueryData<ThreadDetail>(qk.thread(id), (prev) => (prev ? { ...prev, thread: { ...prev.thread, ...patch } } : prev));
}

/** PATCH /api/threads/:id (flag / unread) with optimistic update. */
export function useUpdateThread() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; flagged?: boolean; unread?: boolean }) => api('PATCH /api/threads/:id', { params: { id }, body }),
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: qk.threadsRoot });
      const lists = qc.getQueriesData<InfiniteData<ListRes>>({ queryKey: qk.threadsRoot });
      const detail = qc.getQueryData<ThreadDetail>(qk.thread(id));
      patchThreadCaches(qc, id, patch);
      return { lists, detail, id };
    },
    onError: (err, _v, ctx) => {
      ctx?.lists.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.detail) qc.setQueryData(qk.thread(ctx.id), ctx.detail);
      toast.error(err);
    },
    onSettled: (_d, _e, v) => {
      if (v.flagged !== undefined) void qc.invalidateQueries({ queryKey: qk.threads({ view: 'flagged' }) });
      void qc.invalidateQueries({ queryKey: ['folders'] });
    },
  });
}
